import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { fn, concat } from '@ember/helper';
import { tracked } from '@glimmer/tracking';
import {
  PhFile,
  PhFileCsv,
  PhFileDoc,
  PhFileImage,
  PhFileJpg,
  PhFilePdf,
  PhFilePng,
  PhFilePpt,
  PhFileText,
  PhFileTxt,
  PhFileXls,
  PhFileZip,
  PhCheckCircle,
  PhWarning,
  PhTrash,
  PhCaretDown,
  PhCaretUp,
} from 'ember-phosphor-icons';
import Chip from 'tio-reimagined/components/chip';
import { Section } from 'tio-reimagined/components/layout';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import { eq, or } from 'tio-ui/utilities';
import { t } from 'ember-intl';
import { service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import safeFormatDate from '../../../../../helpers/safe-format-date.ts';
import safeFormatNumber from '../../../../../helpers/safe-format-number.ts';
import type TasAssetModel from 'tio-data/models/tas-asset';
import type AiRecommendationModel from 'tio-data/models/ai-recommendation';
import type AssetSummaryModel from 'tio-data/models/asset-summary';
import type { TOC } from '@ember/component/template-only';

// Co-located inline "warning + Not found" marker. Rendered ~11× across the
// certificate / receipt / transcript detail rows for missing fields; one
// component replaces the repeated warning-colored span markup.
const NotFound: TOC<{ Element: HTMLSpanElement }> = <template>
  <span class="tio-row-extra-tight app-icon-warning font-semibold" ...attributes>
    <PhWarning @size="16" @weight="fill" />
    {{t "common.tas.approval.drawer.attachments.analysis.not_found"}}
  </span>
</template>;

/**
 * Attachments Analysis View
 * Displays AI-generated document summaries for uploaded attachments
 * Now uses asset-summary as primary data source
 */
export interface AttachmentsAnalysisViewSignature {
  Args: {
    recommendation?: AiRecommendationModel;
    attachments: TasAssetModel[];
    assetSummaries?: AssetSummaryModel[];
    deleteAttachment?: {
      perform: (asset: TasAssetModel) => Promise<void>;
      isRunning: boolean;
    };
    isDeleted?: (asset: TasAssetModel) => boolean;
  };
}

interface TranscriptInfo {
  courses?: Array<{
    grade?: string;
    course?: string;
    period?: string;
    credits?: number;
    course_detail?: string;
  }>;
  program?: string;
  summary?: string;
  institution?: string;
  student_name?: string;
}

interface ReceiptInfo {
  entity?: string;
  summary?: string;
  line_items?: Array<{
    line_date?: string;
    line_item?: string;
    line_type?: string;
    line_amount?: number;
    line_details?: string;
  }>;
  payer_name?: string;
}

interface CertificateInfo {
  summary?: string;
  institution?: string;
  student_name?: string;
  completion_date?: string;
  degree_or_certification?: string;
  degreeOrCertification?: string;
  program?: string;
}

interface DocumentResult {
  general?: {
    summary: string;
  };
  transcript?: TranscriptInfo;
  receipt?: ReceiptInfo;
  certificate?: CertificateInfo;
}

interface DocumentSummary {
  result?: DocumentResult;
  is_error?: boolean;
  file_name?: string;
}

class AttachmentsAnalysisView extends Component<AttachmentsAnalysisViewSignature> {
  @service declare intl: IntlService;

  @tracked attachmentToDelete: TasAssetModel | null = null;
  @tracked showConfirmModal = false;
  // Tracks which attachment summaries are expanded (collapsed by default)
  @tracked expandedSummaries = new Set<string>();
  // Tracks which discarded attachment cards are expanded (collapsed by default)
  @tracked expandedCards = new Set<string>();

  // Deduplicate attachments by ID (the caller may pass the same record via multiple relationships)
  get uniqueAttachments(): TasAssetModel[] {
    const seenIds = new Set<string>();
    const deduped: TasAssetModel[] = [];
    for (const attachment of this.args.attachments) {
      if (attachment.id && !seenIds.has(attachment.id)) {
        seenIds.add(attachment.id);
        deduped.push(attachment);
      }
    }
    return deduped;
  }

  // Active (non-deleted) attachments — deduped by etag + filename so identical re-uploads
  // with the same name collapse, but same content with different filenames stay separate.
  // Prefers CARD_USAGE_PROOF classification, then most recent upload.
  get activeAttachments(): TasAssetModel[] {
    const { isDeleted } = this.args;
    const active = isDeleted
      ? this.uniqueAttachments.filter((a) => !isDeleted(a))
      : this.uniqueAttachments;

    const byKey = new Map<string, TasAssetModel>();
    for (const attachment of active) {
      const etag = attachment.assetSummary?.etag;
      // Dedup by etag + filename so identical re-uploads collapse,
      // but files with different names (same content) stay separate.
      const key = etag ? `etag:${etag}:${attachment.filename}` : `id:${attachment.id}`;
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, attachment);
      } else {
        // Prefer CARD_USAGE_PROOF — it has special UI treatment (warning chip).
        const existingIsCardUsage = existing.classification === 'CARD_USAGE_PROOF';
        const currentIsCardUsage = attachment.classification === 'CARD_USAGE_PROOF';
        if (currentIsCardUsage && !existingIsCardUsage) {
          byKey.set(key, attachment);
        } else if (!currentIsCardUsage && existingIsCardUsage) {
          // keep existing
        } else {
          // Same priority — keep most recent
          const existingDate = existing.createdAt ? new Date(existing.createdAt).getTime() : 0;
          const currentDate = attachment.createdAt ? new Date(attachment.createdAt).getTime() : 0;
          if (currentDate > existingDate) {
            byKey.set(key, attachment);
          }
        }
      }
    }
    return [...byKey.values()];
  }

  // Discarded (deleted) attachments — shown individually (each deletion is a distinct event)
  get discardedAttachments(): TasAssetModel[] {
    const { isDeleted } = this.args;
    if (!isDeleted) return [];
    return this.uniqueAttachments.filter((a) => !!isDeleted(a));
  }

  // Sorted: active first, then discarded — single list for one loop
  get sortedAttachments(): TasAssetModel[] {
    return [...this.activeAttachments, ...this.discardedAttachments];
  }

  toggleCard = (id: string | null) => {
    if (!id) return;
    if (this.expandedCards.has(id)) {
      this.expandedCards.delete(id);
    } else {
      this.expandedCards.add(id);
    }
    this.expandedCards = new Set(this.expandedCards);
  };

  isCardExpanded = (id: string | null): boolean => {
    if (!id) return false;
    return this.expandedCards.has(id);
  };

  // Whether the card body should be visible — always true for active, requires expand for discarded
  isBodyVisible = (attachment: TasAssetModel): boolean => {
    if (!this.args.isDeleted || !this.args.isDeleted(attachment)) return true;
    return this.expandedCards.has(attachment.id ?? '');
  };

  // Whether this is the first discarded attachment (used to render separator)
  isFirstDiscarded = (attachment: TasAssetModel): boolean => {
    const discarded = this.discardedAttachments;
    return discarded.length > 0 && discarded[0]?.id === attachment.id;
  };

  toggleSummary = (key: string | null) => {
    if (!key) return;
    if (this.expandedSummaries.has(key)) {
      this.expandedSummaries.delete(key);
    } else {
      this.expandedSummaries.add(key);
    }
    // Force reactivity by reassigning
    this.expandedSummaries = new Set(this.expandedSummaries);
  };

  isSummaryExpanded = (key: string | null): boolean => {
    if (!key) return false;
    return this.expandedSummaries.has(key);
  };

  confirmDelete = (attachment: TasAssetModel) => {
    this.attachmentToDelete = attachment;
    this.showConfirmModal = true;
  };

  cancelDelete = () => {
    this.attachmentToDelete = null;
    this.showConfirmModal = false;
  };

  performDelete = async () => {
    if (this.attachmentToDelete && this.args.deleteAttachment) {
      await this.args.deleteAttachment.perform(this.attachmentToDelete);
      this.attachmentToDelete = null;
      this.showConfirmModal = false;
    }
  };

  /**
   * Get asset summary for a specific attachment
   * This is now the primary source of document analysis data
   */
  getAssetSummaryForAttachment = (attachment: TasAssetModel): AssetSummaryModel | null => {
    try {
      if (!attachment) {
        return null;
      }

      // First try to get from the attachment's direct relationship
      if (attachment.assetSummary) {
        return attachment.assetSummary;
      }

      // Fallback to searching in the passed array
      if (this.args.assetSummaries) {
        const summary = this.args.assetSummaries.find(
          (summary) => summary.digitalAsset?.id === attachment.id
        );
        return summary || null;
      }

      return null;
    } catch (error) {
      console.error('Error getting asset summary for attachment:', error);
      return null;
    }
  };

  /**
   * Extract document summary from asset summary
   * Asset summary.summary contains the full analysis including result, is_error, file_name
   */
  getDocumentSummaryFromAssetSummary = (
    assetSummary: AssetSummaryModel
  ): DocumentSummary | null => {
    try {
      if (!assetSummary) {
        return null;
      }

      if (!assetSummary.summary) {
        return null;
      }

      // The summary attribute contains the full DocumentSummary structure
      // Cast it to DocumentSummary type
      const summary = assetSummary.summary as unknown as DocumentSummary;

      return summary;
    } catch (error) {
      console.error('Error extracting document summary from asset summary:', error);
      return null;
    }
  };

  openAttachment = (url: string) => {
    window.open(url, '_blank');
  };

  /**
   * Get the appropriate file icon component based on file extension
   */
  getFileIcon = (attachment: TasAssetModel) => {
    const extension = attachment.extension?.toLowerCase();

    switch (extension) {
      case 'pdf':
        return PhFilePdf;
      case 'doc':
      case 'docx':
        return PhFileDoc;
      case 'xls':
      case 'xlsx':
        return PhFileXls;
      case 'ppt':
      case 'pptx':
        return PhFilePpt;
      case 'txt':
        return PhFileTxt;
      case 'zip':
        return PhFileZip;
      case 'jpg':
      case 'jpeg':
        return PhFileJpg;
      case 'png':
        return PhFilePng;
      case 'gif':
      case 'bmp':
      case 'svg':
        return PhFileImage;
      case 'csv':
        return PhFileCsv;
      case 'text':
        return PhFileText;
      default:
        return PhFile;
    }
  };

  /**
   * Get human-readable document type label and intent for chip
   * Infers document type from AI analysis structure and summary text
   */
  getDocumentTypeInfo = (
    docSummary?: DocumentSummary
  ): { label: string; intent: 'success' | 'warning' | 'default' | 'primary' | 'danger' } => {
    const prefix = 'common.tas.approval.drawer.attachments.analysis';

    if (!docSummary?.result) {
      return { label: this.intl.t(`${prefix}.doc_type_unknown`), intent: 'default' };
    }

    // Check structured data first (most reliable)
    if (docSummary.result.receipt) {
      return { label: this.intl.t(`${prefix}.doc_type_receipt`), intent: 'success' };
    }
    if (docSummary.result.transcript) {
      return { label: this.intl.t(`${prefix}.doc_type_transcript`), intent: 'primary' };
    }

    // Check general summary text for keywords
    const summary = docSummary.result.general?.summary?.toLowerCase() || '';

    if (summary.includes('receipt') || summary.includes('invoice') || summary.includes('payment')) {
      return { label: this.intl.t(`${prefix}.doc_type_receipt`), intent: 'success' };
    }
    if (summary.includes('transcript')) {
      return { label: this.intl.t(`${prefix}.doc_type_transcript`), intent: 'primary' };
    }
    if (
      summary.includes('enrollment') ||
      summary.includes('enrolment') ||
      summary.includes('proof of enrollment') ||
      summary.includes('enrollment verification')
    ) {
      return { label: this.intl.t(`${prefix}.doc_type_enrollment`), intent: 'primary' };
    }
    if (summary.includes('grade report') || summary.includes('report card')) {
      return { label: this.intl.t(`${prefix}.doc_type_grade_report`), intent: 'primary' };
    }
    if (summary.includes('syllabus')) {
      return { label: this.intl.t(`${prefix}.doc_type_syllabus`), intent: 'default' };
    }
    if (
      summary.includes('completion') ||
      summary.includes('certificate') ||
      summary.includes('certification')
    ) {
      return { label: this.intl.t(`${prefix}.doc_type_completion`), intent: 'success' };
    }
    if (summary.includes('bill') || summary.includes('tuition statement')) {
      return { label: this.intl.t(`${prefix}.doc_type_tuition_bill`), intent: 'success' };
    }

    // Fallback to generic document
    return { label: this.intl.t(`${prefix}.doc_type_document`), intent: 'default' };
  };

  <template>
    <div class="p-2 overflow-x-hidden">
      <h3 class="text-base font-semibold mb-4 tio-fg-primary">{{t
          "common.tas.approval.drawer.attachments.analysis.document_analysis"
        }}</h3>
      <p class="text-sm tio-fg-secondary mb-6">
        {{t "common.tas.approval.drawer.attachments.analysis.description"}}
      </p>

      <div class="space-y-4">
        {{#if this.sortedAttachments.length}}
          {{#each this.sortedAttachments as |attachment|}}
            {{! Render separator before the first discarded attachment }}
            {{#if (this.isFirstDiscarded attachment)}}
              <div class="mt-6 mb-3 border-t tio-border-default pt-4">
                <h4 class="text-xs font-semibold tio-fg-disabled uppercase tracking-wide">
                  {{t
                    "common.tas.approval.drawer.attachments.analysis.discarded"
                    count=this.discardedAttachments.length
                  }}
                </h4>
              </div>
            {{/if}}

            {{! Get asset summary for this attachment }}
            {{#let (this.getAssetSummaryForAttachment attachment) as |assetSummary|}}
              {{#if assetSummary}}
                {{! Extract document summary from asset summary }}
                {{#let (this.getDocumentSummaryFromAssetSummary assetSummary) as |docSummary|}}
                  {{#if docSummary}}
                    <Section>
                      <:header>
                        <div class="tio-col-tight">
                          <div class="tio-row-tight w-full">
                            {{#if (@isDeleted attachment)}}
                              <div class="tio-col-extra-tight flex-1 min-w-0">
                                <div class="tio-row-tight tio-fg-muted italic min-w-0">
                                  {{#let (this.getFileIcon attachment) as |FileIcon|}}
                                    {{#if attachment.signedUrl}}
                                      <a
                                        href={{attachment.signedUrl}}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="tio-row-tight min-w-0 hover:opacity-70 transition-opacity"
                                        title={{t
                                          "common.tas.approval.drawer.attachments.analysis.view_file"
                                          fileName=attachment.filename
                                        }}
                                      >
                                        <FileIcon @size="24" class="shrink-0" />
                                        <span class="block truncate">{{attachment.filename}}</span>
                                      </a>
                                    {{else}}
                                      <FileIcon @size="24" class="shrink-0" />
                                      <span class="block truncate">{{attachment.filename}}</span>
                                    {{/if}}
                                  {{/let}}
                                  <span class="shrink-0 text-sm">{{t
                                      "common.tas.approval.drawer.attachments.analysis.status_deleted"
                                    }}</span>
                                </div>
                                {{#if attachment.attachmentDeletedAt}}
                                  <div class="text-xs tio-fg-disabled dark:text-gray-600 ml-7">
                                    {{t
                                      "common.tas.approval.drawer.attachments.analysis.deleted_label"
                                    }}
                                    {{safeFormatDate
                                      attachment.attachmentDeletedAt
                                      month="numeric"
                                      day="numeric"
                                      year="2-digit"
                                      hour="numeric"
                                      minute="2-digit"
                                    }}
                                  </div>
                                {{/if}}
                              </div>
                              <button
                                type="button"
                                class="p-1 tio-fg-disabled hover:text-gray-600 transition-colors shrink-0"
                                title={{t
                                  "common.tas.approval.drawer.attachments.analysis.toggle_details"
                                }}
                                {{on "click" (fn this.toggleCard attachment.id)}}
                              >
                                {{#if (this.isCardExpanded attachment.id)}}
                                  <PhCaretUp @size="16" />
                                {{else}}
                                  <PhCaretDown @size="16" />
                                {{/if}}
                              </button>
                            {{else}}
                              <div class="tio-col-extra-tight flex-1 min-w-0">
                                <div class="tio-row-tight min-w-0 overflow-hidden">
                                  {{#let (this.getFileIcon attachment) as |FileIcon|}}
                                    {{#if attachment.signedUrl}}
                                      <a
                                        href={{attachment.signedUrl}}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="tio-row-tight min-w-0 hover:opacity-70 transition-opacity max-w-96"
                                        title={{t
                                          "common.tas.approval.drawer.attachments.analysis.view_file"
                                          fileName=attachment.filename
                                        }}
                                      >
                                        <FileIcon @size="24" class="text-purple-600 shrink-0" />
                                        <span
                                          class="font-semibold block truncate"
                                        >{{attachment.filename}}</span>
                                      </a>
                                    {{else}}
                                      <FileIcon @size="24" class="text-purple-600 shrink-0" />
                                      <span
                                        class="font-semibold block truncate max-w-96"
                                      >{{attachment.filename}}</span>
                                    {{/if}}
                                  {{/let}}
                                </div>
                                {{#if attachment.createdAt}}
                                  <div class="tio-text-caption ml-7">
                                    {{t
                                      "common.tas.approval.drawer.attachments.analysis.uploaded_label"
                                    }}
                                    {{safeFormatDate
                                      attachment.createdAt
                                      month="numeric"
                                      day="numeric"
                                      year="2-digit"
                                      hour="numeric"
                                      minute="2-digit"
                                    }}
                                  </div>
                                {{/if}}
                              </div>
                              <div class="tio-row-tight shrink-0 ml-auto">
                                {{#if (eq attachment.classification "CARD_USAGE_PROOF")}}
                                  <Chip @intent="warning" @size="sm">{{t
                                      "common.tas.approval.drawer.attachments.card_usage_receipt"
                                    }}</Chip>
                                {{/if}}
                                {{#if @deleteAttachment}}
                                  <button
                                    type="button"
                                    class="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 dark:bg-red-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={{t
                                      "common.tas.approval.drawer.attachments.analysis.delete_attachment_label"
                                    }}
                                    aria-label={{t
                                      "common.tas.approval.drawer.attachments.analysis.delete_attachment_label"
                                    }}
                                    {{on "click" (fn this.confirmDelete attachment)}}
                                  >
                                    <PhTrash @size="18" />
                                  </button>
                                {{/if}}
                              </div>
                            {{/if}}
                          </div>
                        </div>
                      </:header>
                      <:body>
                        {{#if (this.isBodyVisible attachment)}}
                          <div class="break-words">
                            {{! Details section }}
                            {{#if
                              (or
                                docSummary.result.receipt
                                docSummary.result.transcript
                                docSummary.result.certificate
                                docSummary.is_error
                              )
                            }}
                              {{! Certificate Information }}
                              {{#if docSummary.result.certificate}}
                                <div class="app-status-panel app-status-panel--success mb-4 p-3">
                                  <h4
                                    class="tio-row-tight text-sm font-semibold tio-fg-secondary mb-2"
                                  >
                                    <PhCheckCircle @size="16" class="app-icon-success" />
                                    {{t
                                      "common.tas.approval.drawer.attachments.analysis.certificate_details"
                                      default="Certificate Details"
                                    }}
                                  </h4>

                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.completion_date"
                                        default="Completion Date:"
                                      }}</span>
                                    {{#if docSummary.result.certificate.completion_date}}
                                      <span class="tio-fg-secondary">{{safeFormatDate
                                          docSummary.result.certificate.completion_date
                                          month="short"
                                          day="numeric"
                                          year="numeric"
                                        }}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>

                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.credential"
                                        default="Credential:"
                                      }}</span>
                                    {{#if
                                      (or
                                        docSummary.result.certificate.degree_or_certification
                                        docSummary.result.certificate.degreeOrCertification
                                      )
                                    }}
                                      <span class="tio-fg-secondary">{{or
                                          docSummary.result.certificate.degree_or_certification
                                          docSummary.result.certificate.degreeOrCertification
                                        }}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>

                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.institution"
                                      }}</span>
                                    {{#if docSummary.result.certificate.institution}}
                                      <span
                                        class="tio-fg-secondary"
                                      >{{docSummary.result.certificate.institution}}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>

                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.student"
                                      }}</span>
                                    {{#if docSummary.result.certificate.student_name}}
                                      <span
                                        class="tio-fg-secondary"
                                      >{{docSummary.result.certificate.student_name}}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>

                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.program"
                                      }}</span>
                                    {{#if docSummary.result.certificate.program}}
                                      <span
                                        class="tio-fg-secondary"
                                      >{{docSummary.result.certificate.program}}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>
                                </div>
                              {{/if}}

                              {{! Payment Information }}
                              {{#if docSummary.result.receipt}}
                                <div
                                  class="mb-4 p-3 bg-green-50 border border-green-100 rounded-lg"
                                >
                                  <h4
                                    class="tio-row-tight text-sm font-semibold tio-fg-secondary mb-2"
                                  >
                                    <PhCheckCircle @size="16" class="text-green-600" />
                                    {{t
                                      "common.tas.approval.drawer.attachments.analysis.payment_details"
                                    }}
                                  </h4>
                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.entity"
                                      }}</span>
                                    {{#if docSummary.result.receipt.entity}}
                                      <span
                                        class="tio-fg-secondary"
                                      >{{docSummary.result.receipt.entity}}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>
                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.payer"
                                      }}</span>
                                    {{#if docSummary.result.receipt.payer_name}}
                                      <span
                                        class="tio-fg-secondary"
                                      >{{docSummary.result.receipt.payer_name}}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>
                                  {{#if docSummary.result.receipt.line_items}}
                                    <div class="mt-2 space-y-1">
                                      {{#each docSummary.result.receipt.line_items as |lineItem|}}
                                        <div
                                          class="text-xs p-2 tio-bg-surface rounded border border-green-200"
                                        >
                                          <div class="tio-row-between mb-1">
                                            <span
                                              class="font-semibold"
                                            >{{lineItem.line_item}}</span>
                                            {{#if lineItem.line_amount}}
                                              <span class="text-green-700 font-bold">
                                                {{safeFormatNumber
                                                  lineItem.line_amount
                                                  style="currency"
                                                  currency="USD"
                                                }}
                                              </span>
                                            {{/if}}
                                          </div>
                                          {{#if lineItem.line_details}}
                                            <p
                                              class="tio-fg-secondary"
                                            >{{lineItem.line_details}}</p>
                                          {{/if}}
                                        </div>
                                      {{/each}}
                                    </div>
                                  {{else}}
                                    <div
                                      class="tio-row-extra-tight app-icon-warning mt-2 text-xs font-semibold"
                                    >
                                      <PhWarning @size="16" @weight="fill" />
                                      <span>{{t
                                          "common.tas.approval.drawer.attachments.analysis.no_line_items"
                                        }}</span>
                                    </div>
                                  {{/if}}
                                  {{#if docSummary.result.receipt.summary}}
                                    <button
                                      type="button"
                                      class="tio-row-tight text-xs font-semibold text-green-700 hover:text-green-800 transition-colors mt-3"
                                      {{on
                                        "click"
                                        (fn this.toggleSummary (concat attachment.id "-receipt"))
                                      }}
                                    >
                                      <span>{{t
                                          "common.tas.approval.drawer.attachments.analysis.summary"
                                        }}</span>
                                      {{#if
                                        (this.isSummaryExpanded (concat attachment.id "-receipt"))
                                      }}
                                        <PhCaretUp @size="14" />
                                      {{else}}
                                        <PhCaretDown @size="14" />
                                      {{/if}}
                                    </button>
                                    {{#if
                                      (this.isSummaryExpanded (concat attachment.id "-receipt"))
                                    }}
                                      <p class="text-xs tio-fg-secondary mt-1">
                                        {{docSummary.result.receipt.summary}}
                                      </p>
                                    {{/if}}
                                  {{/if}}
                                </div>
                              {{/if}}

                              {{! Transcript Information }}
                              {{#if docSummary.result.transcript}}
                                <div class="app-status-panel app-status-panel--info mb-4 p-3">
                                  <h4 class="text-sm font-semibold tio-fg-secondary mb-2">{{t
                                      "common.tas.approval.drawer.attachments.analysis.course_details"
                                    }}</h4>
                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.institution"
                                      }}</span>
                                    {{#if docSummary.result.transcript.institution}}
                                      <span
                                        class="tio-fg-secondary"
                                      >{{docSummary.result.transcript.institution}}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>
                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.student"
                                      }}</span>
                                    {{#if docSummary.result.transcript.student_name}}
                                      <span
                                        class="tio-fg-secondary"
                                      >{{docSummary.result.transcript.student_name}}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>
                                  <p class="tio-row-extra-tight text-xs mb-1">
                                    <span class="font-semibold tio-fg-secondary">{{t
                                        "common.tas.approval.drawer.attachments.analysis.program"
                                      }}</span>
                                    {{#if docSummary.result.transcript.program}}
                                      <span
                                        class="tio-fg-secondary"
                                      >{{docSummary.result.transcript.program}}</span>
                                    {{else}}
                                      <NotFound />
                                    {{/if}}
                                  </p>
                                  {{#if docSummary.result.transcript.courses}}
                                    {{#if docSummary.result.transcript.courses.length}}
                                      <div class="mt-2 space-y-1">
                                        <p class="text-xs font-semibold tio-fg-secondary mb-1">{{t
                                            "common.tas.approval.drawer.attachments.analysis.courses"
                                          }}</p>
                                        {{#each docSummary.result.transcript.courses as |course|}}
                                          <div
                                            class="text-xs p-2 tio-bg-surface rounded border app-divider-info"
                                          >
                                            <div class="tio-row-between">
                                              <span class="font-semibold">{{course.course}}</span>
                                              {{#if course.grade}}
                                                <Chip
                                                  @intent="default"
                                                  @size="sm"
                                                >{{course.grade}}</Chip>
                                              {{else}}
                                                <span
                                                  class="tio-row-extra-tight app-icon-warning font-semibold"
                                                >
                                                  <span>{{t
                                                      "common.tas.approval.drawer.attachments.analysis.grade"
                                                    }}</span>
                                                  <PhWarning @size="16" @weight="fill" />
                                                  <span>{{t
                                                      "common.tas.approval.drawer.attachments.analysis.not_found"
                                                    }}</span>
                                                </span>
                                              {{/if}}
                                            </div>
                                            {{#if course.period}}
                                              <p class="tio-text-caption mt-1">{{course.period}}</p>
                                            {{/if}}
                                            {{#if course.course_detail}}
                                              <p
                                                class="tio-fg-secondary mt-1"
                                              >{{course.course_detail}}</p>
                                            {{/if}}
                                          </div>
                                        {{/each}}
                                      </div>
                                    {{else}}
                                      <div
                                        class="tio-row-extra-tight app-icon-warning mt-2 text-xs font-semibold"
                                      >
                                        <PhWarning @size="16" @weight="fill" />
                                        <span>{{t
                                            "common.tas.approval.drawer.attachments.analysis.no_courses"
                                          }}</span>
                                      </div>
                                    {{/if}}
                                  {{else}}
                                    <div
                                      class="tio-row-extra-tight app-icon-warning mt-2 text-xs font-semibold"
                                    >
                                      <PhWarning @size="16" @weight="fill" />
                                      <span>{{t
                                          "common.tas.approval.drawer.attachments.analysis.no_courses"
                                        }}</span>
                                    </div>
                                  {{/if}}
                                  {{#if docSummary.result.transcript.summary}}
                                    <button
                                      type="button"
                                      class="tio-row-tight app-status-text-info text-xs font-semibold transition-colors mt-3"
                                      {{on
                                        "click"
                                        (fn this.toggleSummary (concat attachment.id "-transcript"))
                                      }}
                                    >
                                      <span>{{t
                                          "common.tas.approval.drawer.attachments.analysis.summary"
                                        }}</span>
                                      {{#if
                                        (this.isSummaryExpanded
                                          (concat attachment.id "-transcript")
                                        )
                                      }}
                                        <PhCaretUp @size="14" />
                                      {{else}}
                                        <PhCaretDown @size="14" />
                                      {{/if}}
                                    </button>
                                    {{#if
                                      (this.isSummaryExpanded (concat attachment.id "-transcript"))
                                    }}
                                      <p class="text-xs tio-fg-secondary mt-1">
                                        {{docSummary.result.transcript.summary}}
                                      </p>
                                    {{/if}}
                                  {{/if}}
                                </div>
                              {{/if}}

                              {{! Error State }}
                              {{#if docSummary.is_error}}
                                <div
                                  class="tio-row-tight p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-lg"
                                >
                                  <PhWarning
                                    @size="16"
                                    class="text-red-600 dark:text-red-400 shrink-0"
                                  />
                                  <p class="text-sm text-red-700">
                                    {{t
                                      "common.tas.approval.drawer.attachments.analysis.error_processing"
                                    }}
                                  </p>
                                </div>
                              {{/if}}
                            {{/if}}

                            {{! Document Summary — collapsed by default, toggleable }}
                            {{#if docSummary.result.general.summary}}
                              <button
                                type="button"
                                class="tio-row-tight text-sm font-semibold text-purple-600 hover:text-purple-700 transition-colors mt-3 mb-2"
                                {{on "click" (fn this.toggleSummary (concat attachment.id "-doc"))}}
                              >
                                {{#let (this.getDocumentTypeInfo docSummary) as |typeInfo|}}
                                  <Chip
                                    @intent={{typeInfo.intent}}
                                    @size="sm"
                                  >{{typeInfo.label}}</Chip>
                                {{/let}}
                                <span>{{t
                                    "common.tas.approval.drawer.attachments.analysis.document_summary"
                                  }}</span>
                                {{#if (this.isSummaryExpanded (concat attachment.id "-doc"))}}
                                  <PhCaretUp @size="16" />
                                {{else}}
                                  <PhCaretDown @size="16" />
                                {{/if}}
                              </button>

                              {{#if (this.isSummaryExpanded (concat attachment.id "-doc"))}}
                                <p class="text-sm tio-fg-secondary leading-relaxed mb-3">
                                  {{docSummary.result.general.summary}}
                                </p>
                              {{/if}}
                            {{/if}}
                          </div>
                        {{/if}}
                      </:body>
                    </Section>
                  {{else}}
                    {{! No document summary available - show basic info }}
                    <Section>
                      <:header>
                        <div class="tio-col-tight">
                          <div class="tio-row-tight w-full">
                            {{#if (@isDeleted attachment)}}
                              <div class="tio-col-extra-tight flex-1 min-w-0">
                                <div class="tio-row-tight tio-fg-muted italic min-w-0">
                                  {{#let (this.getFileIcon attachment) as |FileIcon|}}
                                    <FileIcon @size="24" class="shrink-0" />
                                  {{/let}}
                                  <span class="block truncate">{{attachment.filename}}</span>
                                  <span class="shrink-0 text-sm">{{t
                                      "common.tas.approval.drawer.attachments.analysis.status_deleted"
                                    }}</span>
                                </div>
                                {{#if attachment.attachmentDeletedAt}}
                                  <div class="text-xs tio-fg-disabled dark:text-gray-600 ml-7">
                                    {{t
                                      "common.tas.approval.drawer.attachments.analysis.deleted_label"
                                    }}
                                    {{safeFormatDate
                                      attachment.attachmentDeletedAt
                                      month="numeric"
                                      day="numeric"
                                      year="2-digit"
                                      hour="numeric"
                                      minute="2-digit"
                                    }}
                                  </div>
                                {{/if}}
                              </div>
                              <button
                                type="button"
                                class="p-1 tio-fg-disabled hover:text-gray-600 transition-colors shrink-0"
                                title={{t
                                  "common.tas.approval.drawer.attachments.analysis.toggle_details"
                                }}
                                {{on "click" (fn this.toggleCard attachment.id)}}
                              >
                                {{#if (this.isCardExpanded attachment.id)}}
                                  <PhCaretUp @size="16" />
                                {{else}}
                                  <PhCaretDown @size="16" />
                                {{/if}}
                              </button>
                            {{else}}
                              <div class="tio-col-extra-tight flex-1 min-w-0">
                                <div
                                  class="flex flex-wrap items-center gap-2 min-w-0 overflow-hidden"
                                >
                                  {{#let (this.getFileIcon attachment) as |FileIcon|}}
                                    {{#if attachment.signedUrl}}
                                      <a
                                        href={{attachment.signedUrl}}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="flex flex-wrap items-center gap-2 min-w-0 hover:opacity-70 transition-opacity max-w-96"
                                        title={{t
                                          "common.tas.approval.drawer.attachments.analysis.view_file"
                                          fileName=attachment.filename
                                        }}
                                      >
                                        <FileIcon @size="24" class="tio-fg-muted shrink-0" />
                                        <span
                                          class="font-semibold block truncate"
                                        >{{attachment.filename}}</span>
                                      </a>
                                    {{else}}
                                      <FileIcon @size="24" class="tio-fg-muted shrink-0" />
                                      <span
                                        class="font-semibold block truncate max-w-96"
                                      >{{attachment.filename}}</span>
                                    {{/if}}
                                  {{/let}}
                                </div>
                                {{#if attachment.createdAt}}
                                  <div class="tio-text-caption ml-7">
                                    {{t
                                      "common.tas.approval.drawer.attachments.analysis.uploaded_label"
                                    }}
                                    {{safeFormatDate
                                      attachment.createdAt
                                      month="numeric"
                                      day="numeric"
                                      year="2-digit"
                                      hour="numeric"
                                      minute="2-digit"
                                    }}
                                  </div>
                                {{/if}}
                              </div>
                              <div class="tio-row-tight shrink-0 ml-auto">
                                {{#if (eq attachment.classification "CARD_USAGE_PROOF")}}
                                  <Chip @intent="warning" @size="sm">{{t
                                      "common.tas.approval.drawer.attachments.card_usage_receipt"
                                    }}</Chip>
                                {{/if}}
                                {{#if @deleteAttachment}}
                                  <button
                                    type="button"
                                    class="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 dark:bg-red-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={{t
                                      "common.tas.approval.drawer.attachments.analysis.delete_attachment_label"
                                    }}
                                    aria-label={{t
                                      "common.tas.approval.drawer.attachments.analysis.delete_attachment_label"
                                    }}
                                    {{on "click" (fn this.confirmDelete attachment)}}
                                  >
                                    <PhTrash @size="18" />
                                  </button>
                                {{/if}}
                              </div>
                            {{/if}}
                          </div>
                        </div>
                      </:header>
                      <:body>
                        {{#if (this.isBodyVisible attachment)}}
                          <p class="text-sm tio-fg-muted">
                            {{t "common.tas.approval.drawer.attachments.analysis.processing"}}
                          </p>
                        {{/if}}
                      </:body>
                    </Section>
                  {{/if}}
                {{/let}}
              {{else}}
                {{! No asset summary available }}
                <Section>
                  <:header>
                    <div class="tio-col-tight">
                      <div class="tio-row-tight w-full">
                        {{#if (@isDeleted attachment)}}
                          <div class="tio-col-extra-tight flex-1 min-w-0">
                            <div class="tio-row-tight tio-fg-muted italic min-w-0">
                              {{#let (this.getFileIcon attachment) as |FileIcon|}}
                                {{#if attachment.signedUrl}}
                                  <a
                                    href={{attachment.signedUrl}}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="tio-row-tight min-w-0 hover:opacity-70 transition-opacity"
                                    title={{t
                                      "common.tas.approval.drawer.attachments.analysis.view_file"
                                      fileName=attachment.filename
                                    }}
                                  >
                                    <FileIcon @size="24" class="shrink-0" />
                                    <span class="block truncate">{{attachment.filename}}</span>
                                  </a>
                                {{else}}
                                  <FileIcon @size="24" class="shrink-0" />
                                  <span class="block truncate">{{attachment.filename}}</span>
                                {{/if}}
                              {{/let}}
                              <span class="shrink-0 text-sm">{{t
                                  "common.tas.approval.drawer.attachments.analysis.status_deleted"
                                }}</span>
                            </div>
                            {{#if attachment.attachmentDeletedAt}}
                              <div class="text-xs tio-fg-disabled dark:text-gray-600 ml-7">
                                {{t
                                  "common.tas.approval.drawer.attachments.analysis.deleted_label"
                                }}
                                {{safeFormatDate
                                  attachment.attachmentDeletedAt
                                  month="numeric"
                                  day="numeric"
                                  year="2-digit"
                                  hour="numeric"
                                  minute="2-digit"
                                }}
                              </div>
                            {{/if}}
                          </div>
                          <button
                            type="button"
                            class="p-1 tio-fg-disabled hover:text-gray-600 transition-colors shrink-0"
                            title={{t
                              "common.tas.approval.drawer.attachments.analysis.toggle_details"
                            }}
                            {{on "click" (fn this.toggleCard attachment.id)}}
                          >
                            {{#if (this.isCardExpanded attachment.id)}}
                              <PhCaretUp @size="16" />
                            {{else}}
                              <PhCaretDown @size="16" />
                            {{/if}}
                          </button>
                        {{else}}
                          <div class="tio-col-extra-tight flex-1 min-w-0">
                            <div class="tio-row-tight min-w-0 overflow-hidden">
                              {{#let (this.getFileIcon attachment) as |FileIcon|}}
                                {{#if attachment.signedUrl}}
                                  <a
                                    href={{attachment.signedUrl}}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="tio-row-tight min-w-0 hover:opacity-70 transition-opacity max-w-96"
                                    title={{t
                                      "common.tas.approval.drawer.attachments.analysis.view_file"
                                      fileName=attachment.filename
                                    }}
                                  >
                                    <FileIcon @size="24" class="tio-fg-muted shrink-0" />
                                    <span
                                      class="font-semibold block truncate"
                                    >{{attachment.filename}}</span>
                                  </a>
                                {{else}}
                                  <FileIcon @size="24" class="tio-fg-muted shrink-0" />
                                  <span
                                    class="font-semibold block truncate max-w-96"
                                  >{{attachment.filename}}</span>
                                {{/if}}
                              {{/let}}
                            </div>
                            {{#if attachment.createdAt}}
                              <div class="tio-text-caption ml-7">
                                {{t
                                  "common.tas.approval.drawer.attachments.analysis.uploaded_label"
                                }}
                                {{safeFormatDate
                                  attachment.createdAt
                                  month="numeric"
                                  day="numeric"
                                  year="2-digit"
                                  hour="numeric"
                                  minute="2-digit"
                                }}
                              </div>
                            {{/if}}
                          </div>
                          <div class="tio-row-tight shrink-0 ml-auto">
                            {{#if (eq attachment.classification "CARD_USAGE_PROOF")}}
                              <Chip @intent="warning" @size="sm">{{t
                                  "common.tas.approval.drawer.attachments.card_usage_receipt"
                                }}</Chip>
                            {{/if}}
                            {{#if @deleteAttachment}}
                              <button
                                type="button"
                                class="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 dark:bg-red-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title={{t
                                  "common.tas.approval.drawer.attachments.analysis.delete_attachment_label"
                                }}
                                aria-label={{t
                                  "common.tas.approval.drawer.attachments.analysis.delete_attachment_label"
                                }}
                                {{on "click" (fn this.confirmDelete attachment)}}
                              >
                                <PhTrash @size="18" />
                              </button>
                            {{/if}}
                          </div>
                        {{/if}}
                      </div>
                    </div>
                  </:header>
                  <:body>
                    {{#if (this.isBodyVisible attachment)}}
                      <p class="text-sm tio-fg-muted">
                        {{t "common.tas.approval.drawer.attachments.analysis.no_analysis"}}
                      </p>
                    {{/if}}
                  </:body>
                </Section>
              {{/if}}
            {{/let}}
          {{/each}}
        {{else}}
          <div class="text-center py-8">
            <PhFile @size="48" class="text-gray-300 mx-auto mb-2" />
            <p class="text-sm tio-fg-muted">{{t
                "common.tas.approval.drawer.attachments.analysis.no_attachments"
              }}</p>
          </div>
        {{/if}}
      </div>
    </div>

    {{! Confirmation Modal for Delete }}
    <Modal
      @isOpen={{this.showConfirmModal}}
      @onClose={{this.cancelDelete}}
      @size="sm"
      @ariaLabel={{t "common.aria_labels.delete_attachment"}}
    >
      <:header>
        <h3 class="tio-text-heading">{{t
            "common.tas.approval.drawer.attachments.analysis.delete_title"
          }}</h3>
      </:header>
      <:body>
        <p class="text-sm tio-fg-secondary">
          {{#if this.attachmentToDelete}}
            {{#if this.attachmentToDelete.filename}}
              {{t
                "common.tas.approval.drawer.attachments.analysis.confirm_delete_named"
                fileName=this.attachmentToDelete.filename
              }}
            {{else}}
              {{t "common.tas.approval.drawer.attachments.analysis.confirm_delete_generic"}}
            {{/if}}
          {{else}}
            {{t "common.tas.approval.drawer.attachments.analysis.confirm_delete_generic"}}
          {{/if}}
        </p>
      </:body>
      <:footer>
        <Footer
          @onCancel={{this.cancelDelete}}
          @onSubmit={{this.performDelete}}
          @submitText={{t "common.tas.approval.drawer.attachments.analysis.delete_action"}}
          @submitIntent="danger"
          @cancelText={{t "common.tas.approval.drawer.attachments.analysis.cancel_action"}}
          @isRunning={{@deleteAttachment.isRunning}}
        />
      </:footer>
    </Modal>
  </template>
}

export default AttachmentsAnalysisView;
